import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";
import { colors } from "../../styles/colors";
import { down } from "../../styles/breakpoints";
import {
  pageShellRoot,
  tablePaginationResponsive,
} from "../../styles/pageShell";

const useStyles = makeStyles((theme: Theme) => ({
  contentMain: pageShellRoot,

  /* Lands on <main>, which already carries an inline padding-left for the
     sidebar — so no width here. See pageShell.ts. */
  auditRoot: {
    minWidth: 0,
    padding: "24px",
    backgroundColor: theme?.palette?.background?.default || colors.bgcolor,
    [down.md]: { padding: "20px 16px" },
    [down.sm]: { padding: "16px 12px" },
    ...tablePaginationResponsive,
  },

  containerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "24px",
    padding: "0 8px",
    [down.sm]: {
      flexDirection: "column",
      alignItems: "flex-start",
      marginBottom: "16px",
      padding: 0,
    },
  },

  divIconArrow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  card: {
    "&.MuiPaper-root": {
      backgroundColor: theme?.palette?.background?.paper || colors.WHITE,
      borderRadius: 16,
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
      border: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
      overflow: "hidden",
    },
  },

  cardContent: {
    "&.MuiCardContent-root": {
      padding: 24,
      "&:last-child": { paddingBottom: 24 },
      [down.sm]: {
        padding: 16,
        "&:last-child": { paddingBottom: 16 },
      },
    },
  },

  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },

  statCard: {
    "&.MuiPaper-root": {
      minWidth: 0,
      borderRadius: 12,
      border: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
      boxShadow: "none",
    },
  },

  statValue: {
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1.2,
    fontVariantNumeric: "tabular-nums",
    [down.sm]: { fontSize: 22 },
  },

  statLabel: {
    fontSize: 12,
    color: theme?.palette?.text?.secondary || "#6b7280",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },

  tabsBar: {
    borderBottom: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
    marginBottom: 24,
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 600,
      minHeight: 48,
    },
  },

  /* Filter bar. Wraps rather than scrolls: a filter that has scrolled out of
     sight is how an operator ends up believing the table is empty when it is
     merely filtered. */
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center",
    marginBottom: 16,
    "& .MuiFormControl-root": { minWidth: 150 },
    [down.sm]: {
      "& .MuiFormControl-root": { minWidth: 0, flex: "1 1 140px" },
    },
  },

  filterSpacer: { flex: 1, [down.sm]: { display: "none" } },

  /* The table needs ~1000px on one line. It scrolls horizontally rather than
     dropping columns, so the page itself never overflows. */
  tableWrap: {
    "&.MuiPaper-root": {
      borderRadius: 8,
      border: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
      boxShadow: "none",
      overflowX: "auto",
    },
  },

  /* Rows open the detail drawer, so they need to look clickable. Without the
     cursor change the drawer is a feature nobody discovers. */
  clickableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme?.palette?.action?.hover || "rgba(0,0,0,0.04)",
    },
  },

  /* A critical row is tinted as well as chipped. Colour on a small chip is
     easy to miss when scanning fifty rows, and these are the rows that must
     not be missed. */
  criticalRow: {
    backgroundColor: "rgba(211, 47, 47, 0.06)",
  },

  actorCell: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },

  actorName: {
    fontWeight: 600,
    fontSize: 13,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  actorEmail: {
    fontSize: 11,
    color: theme?.palette?.text?.secondary || "#6b7280",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  mono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
  },

  monoSmall: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 11,
    color: theme?.palette?.text?.secondary || "#6b7280",
  },

  /* ── Detail drawer ──────────────────────────────────────────────────── */

  drawerPaper: {
    "&.MuiDrawer-paper": {
      width: 640,
      maxWidth: "100vw",
      padding: 0,
    },
  },

  drawerHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    padding: "20px 24px",
    borderBottom: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: theme?.palette?.background?.paper || colors.WHITE,
  },

  drawerBody: {
    padding: 24,
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: theme?.palette?.text?.secondary || "#6b7280",
    margin: "24px 0 12px",
  },

  sectionTitleFirst: {
    marginTop: 0,
  },

  metaGrid: {
    display: "grid",
    gridTemplateColumns: "140px 1fr",
    gap: "8px 16px",
    fontSize: 13,
    [down.sm]: { gridTemplateColumns: "1fr" },
  },

  metaKey: {
    color: theme?.palette?.text?.secondary || "#6b7280",
  },

  metaValue: {
    minWidth: 0,
    overflowWrap: "anywhere",
  },

  /* ── Diff viewer ─────────────────────────────────────────────────────
     Three columns: field, before, after. This is the heart of the screen —
     "the price changed" is an alert, "990 became 1490" is an answer. */
  diffRow: {
    display: "grid",
    gridTemplateColumns: "minmax(120px, 1fr) minmax(0, 1.2fr) minmax(0, 1.2fr)",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
    marginBottom: 8,
    alignItems: "start",
    [down.sm]: { gridTemplateColumns: "1fr" },
  },

  diffField: {
    fontWeight: 600,
    fontSize: 13,
    overflowWrap: "anywhere",
  },

  diffOld: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    padding: "4px 8px",
    borderRadius: 4,
    backgroundColor: "rgba(211, 47, 47, 0.08)",
    color: "#8b2020",
    overflowWrap: "anywhere",
  },

  diffNew: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    padding: "4px 8px",
    borderRadius: 4,
    backgroundColor: "rgba(46, 125, 50, 0.08)",
    color: "#1b5e20",
    overflowWrap: "anywhere",
  },

  /* A redacted value must read as deliberate policy, not as missing data —
     otherwise it gets reported as a bug every single time. */
  redacted: {
    fontStyle: "italic",
    color: theme?.palette?.text?.secondary || "#6b7280",
    backgroundColor: "transparent",
  },

  emptyValue: {
    fontStyle: "italic",
    color: theme?.palette?.text?.disabled || "#9ca3af",
  },

  /* Full error message. Selectable and wrapping, because the first thing an
     operator does with it is paste it into a ticket or a provider support
     thread — an ellipsis defeats both. */
  messageBlock: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12.5,
    lineHeight: 1.6,
    padding: "10px 12px",
    borderRadius: 8,
    backgroundColor: "rgba(211, 47, 47, 0.06)",
    border: "1px solid rgba(211, 47, 47, 0.2)",
    color: "#7f1d1d",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    userSelect: "text",
  },

  jsonBlock: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 11,
    lineHeight: 1.6,
    padding: 12,
    borderRadius: 8,
    backgroundColor: theme?.palette?.grey?.[50] || "#fafafa",
    border: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    margin: 0,
    maxHeight: 320,
  },

  relatedItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 8,
    border: `1px solid ${theme?.palette?.divider || "#e0e0e0"}`,
    marginBottom: 8,
    cursor: "pointer",
    fontSize: 13,
    "&:hover": {
      backgroundColor: theme?.palette?.action?.hover || "rgba(0,0,0,0.04)",
    },
  },

  emptyState: {
    padding: "48px 24px",
    textAlign: "center",
    color: theme?.palette?.text?.secondary || "#6b7280",
  },

  /* Sparkline for the 30-day activity curve. CSS bars rather than a charting
     dependency: it conveys shape, and shape is all a 30-day count needs to
     convey. */
  sparkline: {
    display: "flex",
    alignItems: "flex-end",
    gap: 2,
    height: 40,
    marginTop: 8,
  },

  sparkBar: {
    flex: 1,
    minWidth: 2,
    borderRadius: "2px 2px 0 0",
    backgroundColor: theme?.palette?.primary?.main || "#1976d2",
    opacity: 0.75,
    transition: "opacity .15s ease",
    "&:hover": { opacity: 1 },
  },
}));

export default useStyles;
